import { Link } from 'react-router-dom';
import { BUILDING_NAMES, type BuildingZone } from '@kaeliux/shared';
import { Panel } from '../ui/Panel';
import { Icon, type IconName } from '../ui/Icon';
import { usePlanetView } from '../../contexts/PlanetViewContext';

export interface PlanetScreenLink {
    to: string;
    icon: IconName;
    label: string;
    /** Queued items badge, hidden at 0. */
    count?: number;
}

const ZONE_LINKS: { zone: BuildingZone; icon: IconName; label: string }[] = [
    { zone: 'resource', icon: 'zone-resource', label: 'Resource Zone' },
    { zone: 'industry', icon: 'zone-industry', label: 'Industry Zone' },
    { zone: 'military', icon: 'zone-military', label: 'Military Zone' },
];

export function personalBuildingLinks(shipQueueCount: number): PlanetScreenLink[] {
    return [
        { to: '/sciences', icon: 'science', label: 'Sciences' },
        { to: '/shipyard', icon: 'shipyard', label: BUILDING_NAMES.shipyard, count: shipQueueCount },
        { to: '/starport', icon: 'starport', label: BUILDING_NAMES.techCenter },
        { to: '/recycling', icon: 'scrap', label: BUILDING_NAMES.recyclingPlant },
        { to: '/trade-center', icon: 'trade-token', label: BUILDING_NAMES.tradeCenter },
    ];
}

export interface PlanetZoneLinksFallbackProps {
    zoneQueueCounts: Record<BuildingZone, number>;
    shipQueueCount: number;
    /** Building screens to list — defaults to a personal planet's. */
    buildingLinks?: PlanetScreenLink[];
}

function LinkRow({ to, icon, label, count = 0 }: PlanetScreenLink) {
    return (
        <Link to={to} className="block rounded-tile bg-space-900 px-4 py-3 text-content-primary hover:bg-space-800">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Icon name={icon} size={20} aria-hidden="true" />
                    <span>{label}</span>
                </div>
                {count > 0 && (
                    <span className="text-xs font-semibold bg-space-700 text-space-200 rounded-full px-2 py-0.5">
                        {count}
                    </span>
                )}
            </div>
        </Link>
    );
}

/**
 * DS-18 scope item 4's small-height fallback (`useSurfaceFits`), and the
 * pre-DS-14 zone-link card this replaces as `/planet`'s primary content —
 * kept, rather than deleted, exactly because the surface can't always
 * render usefully (a landscape phone, say), and this is the one screen
 * that still lists every zone and every non-zone building destination in
 * one place.
 */
export function PlanetZoneLinksFallback({ zoneQueueCounts, shipQueueCount, buildingLinks }: PlanetZoneLinksFallbackProps) {
    const { zoneRoute } = usePlanetView();
    return (
        <Panel data-testid="planet-zone-links-fallback" data-tutorial-id="zone-links" className="space-y-2">
            <div className="pt-2 text-content-primary">Planet Zones</div>
            {ZONE_LINKS.map(({ zone, icon, label }) => (
                <LinkRow key={zone} to={zoneRoute(zone)} icon={icon} label={label} count={zoneQueueCounts[zone]} />
            ))}
            <div className="pt-4 text-content-primary">Buildings</div>
            {(buildingLinks ?? personalBuildingLinks(shipQueueCount)).map((link) => (
                <LinkRow key={link.to} {...link} />
            ))}
        </Panel>
    );
}

export default PlanetZoneLinksFallback;
